import { getTranslatedMessage } from '@/i18n/utils';

import {
  InputValueHelperRule,
  Tenant,
  TenantWithDefault,
} from '@/types/common';
import { Option } from '@/types/components/common';
import { DocumentSection } from '@/types/document';
import {
  EmploymentStatus,
  FundingSource,
  Gender,
  MaritalStatus,
  Profession,
  UserTitle,
} from '@/types/user';

import { isAbove18 } from '@/utils/validation';

export const USERNAME_RULES: InputValueHelperRule[] = [
  {
    text: '5-10 characters',
    i18nKey: 'rules.username.characters',
    isMatched: value =>
      !!value && String(value).length <= 10 && String(value).length >= 5,
  },
  {
    text: 'Contains numbers, letters or both',
    i18nKey: 'rules.username.containLetters',
    isMatched: value =>
      !!value && new RegExp(/[a-zA-Z0-9]+/g).test(String(value)),
  },
  {
    text: "No spaces and special characters such as @ $ % ^ & * ( ) + = ” -  _ '",
    i18nKey: 'rules.username.noSCharacter',
    isMatched: value =>
      !!value && new RegExp(/^[a-zA-Z0-9]+$/g).test(String(value)),
  },
];

export const DATE_OF_BIRTH_RULES: InputValueHelperRule[] = [
  {
    text: 'You must be at least 18 years old to proceed with the application',
    i18nKey: 'rules.dob.above18',
    isMatched: isAbove18,
  },
];

export const getFundingSourceOptions = (tenant?: Tenant): Option[] => {
  switch (tenant) {
    case Tenant.Cayman:
      return [
        {
          text: getTranslatedMessage('fundingSourceOptions.salary'),
          value: FundingSource.Salary,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.saleOfAsset'),
          value: FundingSource.SaleOfAsset,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.savings'),
          value: FundingSource.Savings,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.investment'),
          value: FundingSource.Investments,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.inheritance'),
          value: FundingSource.Inheritance,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.rentalIncome'),
          value: FundingSource.RentalIncome,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.dividends'),
          value: FundingSource.Dividends,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.scholarship'),
          value: FundingSource.Scholarship,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.otherIncome'),
          value: FundingSource.OtherIncome,
        },
      ];
    default:
      return [
        {
          text: getTranslatedMessage('fundingSourceOptions.business'),
          value: FundingSource.Business,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.fromParents'),
          value: FundingSource.FundsFromParents,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.fromSpouse'),
          value: FundingSource.FundsFromSpouse,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.investment'),
          value: FundingSource.Investments,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.pension'),
          value: FundingSource.Pension,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.salary'),
          value: FundingSource.Salary,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.savings'),
          value: FundingSource.Savings,
        },
        {
          text: getTranslatedMessage('fundingSourceOptions.other'),
          value: FundingSource.OtherIncome,
        },
      ];
  }
};

export const GenderText: Record<string, string> = {
  [Gender.Female]: 'Female',
  [Gender.Male]: 'Male',
};

export const DEFAULT_MARITAL_STATUSES: Option[] = [
  {
    value: MaritalStatus.Single,
    text: getTranslatedMessage('maritalStatus.single'),
    i18nKey: 'maritalStatus.single',
  },
  {
    value: MaritalStatus.Married,
    text: getTranslatedMessage('maritalStatus.married'),
    i18nKey: 'maritalStatus.married',
  },
  {
    value: MaritalStatus.CommonLaw,
    text: getTranslatedMessage('maritalStatus.commonLaw'),
    i18nKey: 'maritalStatus.commonLaw',
  },
  {
    value: MaritalStatus.Divorced,
    text: getTranslatedMessage('maritalStatus.divorced'),
    i18nKey: 'maritalStatus.divorced',
  },
];

export const getTenantMaritalStatusOrder = (
  tenant?: TenantWithDefault,
): Option[] => {
  switch (tenant) {
    case Tenant.Cayman:
      return [
        {
          value: MaritalStatus.Married,
          text: getTranslatedMessage('maritalStatus.married'),
          i18nKey: 'maritalStatus.married',
        },
        {
          value: MaritalStatus.Divorced,
          text: getTranslatedMessage('maritalStatus.divorced'),
          i18nKey: 'maritalStatus.divorced',
        },
        {
          value: MaritalStatus.Widow,
          text: getTranslatedMessage('maritalStatus.widowed'),
          i18nKey: 'maritalStatus.widowed',
        },
        {
          value: MaritalStatus.Unmarried,
          text: getTranslatedMessage('maritalStatus.unmarried'),
          i18nKey: 'maritalStatus.unmarried',
        },
        {
          value: MaritalStatus.Other,
          text: getTranslatedMessage('maritalStatus.other'),
          i18nKey: 'maritalStatus.other',
        },
      ];

    case Tenant.Suriname:
      return [
        {
          value: MaritalStatus.Single,
          text: getTranslatedMessage('maritalStatus.single'),
          i18nKey: 'maritalStatus.single',
        },
        {
          value: MaritalStatus.Married,
          text: getTranslatedMessage('maritalStatus.married'),
          i18nKey: 'maritalStatus.married',
        },
        {
          value: MaritalStatus.CommonLaw,
          text: getTranslatedMessage('maritalStatus.commonLaw'),
          i18nKey: 'maritalStatus.commonLaw',
        },
        {
          value: MaritalStatus.Divorced,
          text: getTranslatedMessage('maritalStatus.divorced'),
          i18nKey: 'maritalStatus.divorced',
        },
        {
          value: MaritalStatus.Widow,
          text: getTranslatedMessage('maritalStatus.widow'),
          i18nKey: 'maritalStatus.widow',
        },
      ];

    default:
      return DEFAULT_MARITAL_STATUSES;
  }
};

export const TitleText: Record<string, string> = {
  [UserTitle.DR]: 'Dr.',
  [UserTitle.MR]: 'Mr.',
  [UserTitle.MS]: 'Ms.',
  [UserTitle.MRS]: 'Mrs.',
};

export const GENDERS: Option[] = [
  {
    value: Gender.Male,
    i18nKey: 'gender.male',
    text: GenderText[Gender.Male],
  },
  {
    value: Gender.Female,
    i18nKey: 'gender.female',
    text: GenderText[Gender.Female],
  },
];

export const getUserTitleOptions = (tenant?: Tenant): Option[] => {
  switch (tenant) {
    case Tenant.Cayman:
      return [
        {
          value: UserTitle.DR,
          text: getTranslatedMessage(TitleText[UserTitle.DR]),
        },
        {
          value: UserTitle.MR,
          text: getTranslatedMessage(TitleText[UserTitle.MR]),
        },
        {
          value: UserTitle.MS,
          text: getTranslatedMessage(TitleText[UserTitle.MS]),
        },
        {
          value: UserTitle.MRS,
          text: getTranslatedMessage(TitleText[UserTitle.MRS]),
        },
      ];
    default:
      return [
        {
          value: UserTitle.MR,
          text: getTranslatedMessage(TitleText[UserTitle.MR]),
        },
        {
          value: UserTitle.MS,
          text: getTranslatedMessage(TitleText[UserTitle.MS]),
        },
        {
          value: UserTitle.MRS,
          text: getTranslatedMessage(TitleText[UserTitle.MRS]),
        },
      ];
  }
};
export const EMPLOYMENT_STATUS_TEXT: Record<EmploymentStatus, string> = {
  [EmploymentStatus.Employed]: `employmentStatus.${EmploymentStatus.Employed}`,
  [EmploymentStatus.SelfEmployed]: `employmentStatus.${EmploymentStatus.SelfEmployed}`,
  [EmploymentStatus.Unemployed]: `employmentStatus.${EmploymentStatus.Unemployed}`,
};

export const MARITAL_STATUS_TEXT: Record<MaritalStatus, string> = {
  [MaritalStatus.Single]: 'maritalStatus.single',
  [MaritalStatus.Unmarried]: 'maritalStatus.unmarried',
  [MaritalStatus.Married]: 'maritalStatus.married',
  [MaritalStatus.CommonLaw]: 'maritalStatus.commonLaw',
  [MaritalStatus.Divorced]: 'maritalStatus.divorced',
  [MaritalStatus.Widow]: 'maritalStatus.widow',
  [MaritalStatus.Other]: 'maritalStatus.other',
};

export const PROFESSION_TEXT: Record<Profession | string, string> = {
  [Profession.Housewife]: `professionText.${[Profession.Housewife]}`,
  [Profession.PartnershipLimitedLiability]: `professionText.${[Profession.PartnershipLimitedLiability]}`,
  [Profession.Pensioner]: `professionText.${[Profession.Pensioner]}`,
  [Profession.SoleTrader]: `professionText.${[Profession.SoleTrader]}`,
  [Profession.Student]: `professionText.${[Profession.Student]}`,
  [Profession.Unemployed]: `professionText.${[Profession.Unemployed]}`,
  [Profession.Unincorporated]: `professionText.${[Profession.Unincorporated]}`,
  [Profession.CYM_PartnershipLimitedLiability]: `professionText.${[Profession.CYM_PartnershipLimitedLiability]}`,
  [Profession.CYM_Unincorporated]: `professionText.${[Profession.CYM_Unincorporated]}`,
  [Profession.Employee]: `employerForm.form.employedType.option.${[Profession.Employee]}`,
  [Profession.Director]: `employerForm.form.employedType.option.${[Profession.Director]}`,
};

export const PROFFESSION_QUESTION_FOR_SELF_EMPLOYED: Partial<
  Record<
    Tenant | 'default',
    {
      question: string;
      options: Option[];
      description?: string;
    }
  >
> = {
  default: {
    question: 'Select your type of self-employment',
    options: [
      {
        value: Profession.SoleTrader,
        text: PROFESSION_TEXT[Profession.SoleTrader],
        icon: 'toolbox',
      },
      {
        value: Profession.PartnershipLimitedLiability,
        text: PROFESSION_TEXT[Profession.PartnershipLimitedLiability],
        icon: 'handshake',
      },
      {
        value: Profession.Unincorporated,
        text: PROFESSION_TEXT[Profession.Unincorporated],
        icon: 'user',
      },
    ],
  },
  [Tenant.Suriname]: {
    question: 'professionQuestionText.questions.selfEmployed',
    options: [
      {
        value: Profession.SoleTrader,
        text: PROFESSION_TEXT[Profession.SoleTrader],
        icon: 'toolbox',
      },
      {
        value: Profession.PartnershipLimitedLiability,
        text: PROFESSION_TEXT[Profession.PartnershipLimitedLiability],
        icon: 'handshake',
      },
    ],
  },
  [Tenant.SintMaarten]: {
    question: 'professionQuestionText.questions.selfEmployed',
    options: [
      {
        value: Profession.SoleTrader,
        text: PROFESSION_TEXT[Profession.SoleTrader],
        icon: 'toolbox',
      },
      {
        value: Profession.PartnershipLimitedLiability,
        text: 'professionText.partnershipLimitedLiabilitySXM',
        icon: 'handshake',
      },
    ],
  },
};

export const PROFFESSION_QUESTION_FOR_UNEMPLOYED: Partial<
  Record<
    Tenant | 'default',
    {
      question: string;
      options: Option[];
      description?: string;
    }
  >
> = {
  default: {
    question: 'professionQuestionText.questions.unEmployed',
    options: [
      {
        value: Profession.Student,
        text: PROFESSION_TEXT[Profession.Student],
        icon: 'student',
      },
      {
        value: Profession.Pensioner,
        text: PROFESSION_TEXT[Profession.Pensioner],
        icon: 'glasses',
      },
      {
        value: Profession.Housewife,
        text: PROFESSION_TEXT[Profession.Housewife],
        icon: 'house',
      },
      {
        value: Profession.Unemployed,
        text: PROFESSION_TEXT[Profession.Unemployed],
        icon: 'popcorn',
      },
    ],
  },
};

export const EMPLOYMENT_STATUS_QUESTIONS: Partial<
  Record<Tenant | 'default', Option[]>
> = {
  default: [
    {
      value: EmploymentStatus.Employed,
      text: EMPLOYMENT_STATUS_TEXT[EmploymentStatus.Employed],
      icon: 'buildings',
    },
    {
      value: EmploymentStatus.SelfEmployed,
      text: EMPLOYMENT_STATUS_TEXT[EmploymentStatus.SelfEmployed],
      icon: 'toolbox',
    },
    {
      value: EmploymentStatus.Unemployed,
      text: EMPLOYMENT_STATUS_TEXT[EmploymentStatus.Unemployed],
      icon: 'popcorn',
    },
  ],
  [Tenant.Suriname]: [
    {
      value: EmploymentStatus.Employed,
      text: EMPLOYMENT_STATUS_TEXT[EmploymentStatus.Employed],
      icon: 'buildings',
    },
    {
      value: EmploymentStatus.SelfEmployed,
      text: EMPLOYMENT_STATUS_TEXT[EmploymentStatus.SelfEmployed],
      icon: 'toolbox',
    },
    {
      value: EmploymentStatus.Unemployed,
      text: EMPLOYMENT_STATUS_TEXT[EmploymentStatus.Unemployed],
      icon: 'popcorn',
    },
  ],
};

export const IDENTITY_DOCUMENTS = [
  DocumentSection.Identity,
  DocumentSection.IDVideo,
  DocumentSection.Selfie,
];

export const MINOR_IDENTITY_DOCUMENTS = [DocumentSection.Identity];
